import { TableColumns, TableFilters } from "../../shared/crud/list";
import { StringRenderer } from "../../shared/renderers/table";
import { FormattedMessage, useIntl } from "react-intl";
import { dateFilter, inputFilter, selectorFilter } from "../../shared/crud/filter-widgets";
import { useMemo } from "react";
import { reportFilterMessages, reportMessages } from "../messages";
import {
  GetReportRequest,
  ReportRequest,
  usePropertyCurrencies,
} from "../infrastructure/report.api";
import { asUTC, currentDate, dateToString } from "../../../lib/dateUtils";
import { endOfMonth, parseISO, startOfMonth } from "date-fns";
import { ReportCell, ReportType } from "../index";
import { formatMessage } from "../../../lib/intlUtils";

interface ReportCellCSV {
  message: { id: string; defaultMessage: string };
  value: (entity: ReportCell) => string;
}

export type ReportFilters = Partial<ReportRequest> & {
  period: string;
  order_1: string;
  order_2: string;
  order_3: string;
};

const paymentTypes = [
  "cc_vault",
  "pos",
  "tr",
  "token_vault",
  "pay_no_pain_v2",
  "pci_proxy",
  "paypal_ec",
  "pay_no_pain_proxy",
  "without_card",
  "reservation_request",
  "paycomet",
  "paycomet_ivr",
  "checkout_pnp",
  "adyen",
  "sibs",
  "freedom_pay",
  "stripe"
];

const statusMap = {
  all: {
    status: ["reserva", "cancelada"],
    statusConfig: [0, 1, 2, 3, 4, 5, 6, 7],
  },
  confirmed: {
    status: ["reserva"],
    statusConfig: [0, 1, 2, 3, 4, 5, 6],
  },
  cancelled: {
    status: ["cancelada"],
    statusConfig: [],
  },
  pending: {
    status: ["prereserva"],
    statusConfig: [1],
  },
  failed: {
    status: ["prereserva"],
    statusConfig: [3],
  },
  notConfirmed: {
    status: ["reserva"],
    statusConfig: [4],
  },
  expired: {
    status: ["prereserva"],
    statusConfig: [6],
  },
};

const defaultRequest = {
  checkIn: dateToString(startOfMonth(currentDate())),
  checkOut: dateToString(endOfMonth(currentDate())),
  concept: "affiliate",
  dateSearchCriteria: "booking",
  groupByEntity: "hotel",
  groupDateBy: "period",
  keywordFilter: null,
  orderBy: {},
  status: statusMap.confirmed.status,
  statusConfig: statusMap.confirmed.statusConfig,
  paymentTypes: [],
};

function generateBaseReportHeaders(): TableColumns<ReportCell> {
  return {
    period: {
      renderer: StringRenderer,
      width: 200,
    },
    property: {
      renderer: StringRenderer,
      width: 200,
    },
    partner: {
      renderer: StringRenderer,
      width: 175,
    },
    group: {
      renderer: StringRenderer,
      width: 175,
    },
    avgStay: {
      renderer: StringRenderer,
      width: 100,
    },
    avgAdvance: {
      renderer: StringRenderer,
      width: 100,
    },
    totalReservations: {
      renderer: StringRenderer,
      width: 100,
    },
    totalAmount: {
      renderer: StringRenderer,
      width: 125,
    },
    avgAmount: {
      renderer: StringRenderer,
      width: 100,
    },
    totalRoomsNight: {
      renderer: StringRenderer,
      width: 100,
    },
    avgRoomsNight: {
      renderer: StringRenderer,
      width: 100,
    },
    avgOccupantNight: {
      renderer: StringRenderer,
      width: 100,
    },
    currency: {
      renderer: StringRenderer,
      width: 100,
    },
  };
}

export function generateReportHeaders(
  type: ReportType,
  isWitAdmin: boolean,
  groupByEntity?: string
): TableColumns<ReportCell> {
  const baseReportHeaders = generateBaseReportHeaders();
  if (groupByEntity === "group") {
    delete baseReportHeaders.property;
    delete baseReportHeaders.partner;
  }
  if (groupByEntity === "partner") {
    delete baseReportHeaders.property;
    delete baseReportHeaders.group;
  }
  if (groupByEntity === "all") {
    delete baseReportHeaders.property;
    delete baseReportHeaders.group;
    delete baseReportHeaders.partner;
  }
  if (!isWitAdmin) {
    delete baseReportHeaders.partner;
  }
  switch (type) {
    case ReportType.CONCEPT:
      return {
        concept: {
          renderer: StringRenderer,
          width: 175,
        },
        ...baseReportHeaders,
      };

    case ReportType.STATUS:
      return {
        status: {
          renderer: StringRenderer,
          width: 125,
        },
        ...baseReportHeaders,
      };
    default:
      if (isWitAdmin) {
        return generateBaseReportHeaders();
      } else {
        return baseReportHeaders;
      }
  }
}

export function useReportFilters(
  type: ReportType,
  token: string
): TableFilters<ReportFilters, ReportFilters> {
  const intl = useIntl();
  const { currencies, defaultCurrency } = usePropertyCurrencies(token);

  return useMemo(() => {
    if (!currencies || !defaultCurrency) {
      return {};
    }

    let filters: TableFilters<ReportFilters, ReportFilters> = {
      period: {
        filterFn: "async",
        widget: dateFilter("period", intl),
      },
      dateSearchCriteria: {
        filterFn: "async",
        widget: selectorFilter(<FormattedMessage {...reportFilterMessages.groupDateBy} />, [
          { value: "booking", label: intl.formatMessage(reportFilterMessages.bookingDate) },
          { value: "checkIn", label: intl.formatMessage(reportFilterMessages.checkIn) },
          { value: "checkOut", label: intl.formatMessage(reportFilterMessages.checkOut) },
        ]),
      },
    };

    switch (type) {
      case ReportType.CONCEPT:
        filters.concept = {
          filterFn: "async",
          widget: selectorFilter(<FormattedMessage {...reportFilterMessages.concept} />, [
            { value: "affiliate", label: intl.formatMessage(reportFilterMessages.affiliate) },
            { value: "promoTickers", label: intl.formatMessage(reportFilterMessages.promoTickers) },
            { value: "promoCode", label: intl.formatMessage(reportFilterMessages.promoCode) },
            { value: "extras", label: intl.formatMessage(reportFilterMessages.extras) },
            { value: "country", label: intl.formatMessage(reportFilterMessages.country) },
            {
              value: "accommodation",
              label: intl.formatMessage(reportFilterMessages.accommodation),
            },
            {
              value: "accommodationConf",
              label: intl.formatMessage(reportFilterMessages.accommodationConf),
            },
            {
              value: "conditionName",
              label: intl.formatMessage(reportFilterMessages.conditionName),
            },
            { value: "board", label: intl.formatMessage(reportFilterMessages.board) },
            {
              value: "dynamicPriceTickers",
              label: intl.formatMessage(reportFilterMessages.dynamicPriceTickers),
            },
            { value: "origin", label: intl.formatMessage(reportFilterMessages.origin) },
            { value: "mode", label: intl.formatMessage(reportFilterMessages.mode) },
          ]),
        };
        break;

      case ReportType.STATUS:
        filters.paymentTypes = {
          filterFn: "async",
          widget: selectorFilter(<FormattedMessage {...reportFilterMessages.paymentTypes} />, [
            { value: "", label: intl.formatMessage(reportFilterMessages.all) },
            ...paymentTypes.map(it => ({
              value: it,
              label: intl.formatMessage(reportFilterMessages[it]),
            })),
          ]),
        };
        filters.status = {
          filterFn: "async",
          widget: selectorFilter(<FormattedMessage {...reportFilterMessages.status} />, [
            { value: "all", label: intl.formatMessage(reportFilterMessages.all) },
            { value: "confirmed", label: intl.formatMessage(reportFilterMessages.confirmed) },
            { value: "pending", label: intl.formatMessage(reportFilterMessages.pending) },
            { value: "failed", label: intl.formatMessage(reportFilterMessages.failed) },
            { value: "notConfirmed", label: intl.formatMessage(reportFilterMessages.notConfirmed) },
            { value: "expired", label: intl.formatMessage(reportFilterMessages.expired) },
            { value: "cancelled", label: intl.formatMessage(reportFilterMessages.cancelled) },
          ]),
        };
        break;
    }

    filters = {
      ...filters,
      groupDateBy: {
        filterFn: "async",
        widget: selectorFilter(<FormattedMessage {...reportFilterMessages.dateSearchCriteria} />, [
          { value: "daily", label: intl.formatMessage(reportFilterMessages.perDay) },
          { value: "monthly", label: intl.formatMessage(reportFilterMessages.perMonth) },
          { value: "yearly", label: intl.formatMessage(reportFilterMessages.perYear) },
          { value: "period", label: intl.formatMessage(reportFilterMessages.allPeriod) },
        ]),
      },
      groupByEntity: {
        filterFn: "async",
        widget: selectorFilter(<FormattedMessage {...reportFilterMessages.groupByEntity} />, [
          { value: "hotel", label: intl.formatMessage(reportFilterMessages.byProperty) },
          { value: "partner", label: intl.formatMessage(reportFilterMessages.byPartner) },
          { value: "group", label: intl.formatMessage(reportFilterMessages.byGroup) },
          { value: "all", label: intl.formatMessage(reportFilterMessages.total) },
        ]),
      },
      currency: {
        filterFn: "async",
        widget: selectorFilter(defaultCurrency, [
          { value: defaultCurrency, label: defaultCurrency },
          { value: "original", label: "Original" },
          ...currencies.filter(it => it !== defaultCurrency).map(it => ({ value: it, label: it })),
        ]),
      },
    };

    Array.from({ length: 3 }).forEach((_, i) => {
      filters[`order_${i}`] = {
        filterFn: "async",
        widget: selectorFilter(
          <FormattedMessage {...reportFilterMessages.order} values={{ index: i + 1 }} />,
          [
            { value: "empty", label: "" },
            { value: "period", label: intl.formatMessage(reportMessages.period) },
            { value: "hotelTicker", label: intl.formatMessage(reportMessages.property) },
            { value: "group", label: intl.formatMessage(reportMessages.group) },
            { value: "partner", label: intl.formatMessage(reportMessages.partner) },
            { value: "avgStay", label: intl.formatMessage(reportMessages.avgStay) },
            { value: "avgAdvance", label: intl.formatMessage(reportMessages.avgAdvance) },
            { value: "bookings", label: intl.formatMessage(reportMessages.totalReservations) },
            { value: "totalAmount", label: intl.formatMessage(reportMessages.totalAmount) },
            { value: "avgAmount", label: intl.formatMessage(reportMessages.avgAmount) },
            {
              value: "totalRoomsPerNight",
              label: intl.formatMessage(reportMessages.totalRoomsNight),
            },
            {
              value: "avgAmountRoomPerNight",
              label: intl.formatMessage(reportMessages.avgRoomsNight),
            },
            {
              value: "avgAmountPaxNight",
              label: intl.formatMessage(reportMessages.avgOccupantNight),
            },
            { value: "currency", label: intl.formatMessage(reportMessages.currency) },
          ]
        ),
      };
      filters[`sorting_${i}`] = {
        filterFn: "async",
        widget: selectorFilter(<FormattedMessage {...reportFilterMessages.asc} />, [
          { value: "asc", label: intl.formatMessage(reportFilterMessages.asc) },
          { value: "desc", label: intl.formatMessage(reportFilterMessages.desc) },
        ]),
      };
    });

    filters.keywordFilter = {
      filterFn: "async",
      widget: ({ ...props }) =>
        inputFilter({
          ...props,
          placeholder: intl.formatMessage(reportFilterMessages.filter),
        }),
    };

    return filters;
  }, [intl, currencies, defaultCurrency]);
}

function mapPaymentTypeToStatus(
  paymentTypes?: string[],
  status?: string
): {
  status: string[];
  statusConfig: number[];
} {
  if (!paymentTypes || !status) {
    return {
      status: defaultRequest.status,
      statusConfig: defaultRequest.statusConfig,
    };
  }
  if (paymentTypes.length > 1 || status !== "confirmed") {
    return statusMap[status];
  }
  switch (paymentTypes[0]) {
    case "cc_vault":
      return {
        status: ["reserva"],
        statusConfig: [0],
      };

    case "pos":
    case "token_vault":
    case "pay_no_pain_v2":
    case "pci_proxy":
    case "paypal_ec":
    case "pay_no_pain_proxy":
    case "paycomet":
    case "paycomet_ivr":
    case "checkout_pnp":
    case "adyen":
    case "sibs":
    case "freedom_pay":
    case "stripe":
      return {
        status: ["reserva"],
        statusConfig: [2],
      };

    case "tr":
    case "without_card":
    case "reservation_request":
      return {
        status: ["reserva"],
        statusConfig: [5],
      };

    default:
      return {
        status: defaultRequest.status,
        statusConfig: defaultRequest.statusConfig,
      };
  }
}

export function generateReportRequest({
  filterValues,
  propertyTickers,
  pageSize,
  currentPage,
  type,
  defaultCurrency,
}): GetReportRequest {
  const orderBy: { [key: string]: "asc" | "desc" } = { ...defaultRequest.orderBy };
  Array.from({ length: 3 })
    .filter((_, i) => filterValues?.[`order_${i}`])
    .forEach((_, i) => {
      orderBy[filterValues[`order_${i}`]] = filterValues?.[`sorting_${i}`] || "asc";
    });
  const defaultConcept = type === ReportType.CONCEPT ? defaultRequest.concept : "";
  const filteredPaymentTypes = filterValues?.paymentTypes
    ? filterValues.paymentTypes.split(",")
    : defaultRequest.paymentTypes;
  const statusMap = mapPaymentTypeToStatus(filteredPaymentTypes, filterValues?.status);
  const currency = filterValues?.currency ?? defaultCurrency;
  return {
    request: {
      checkIn: filterValues?.period?.startDate
        ? asUTC(parseISO(filterValues.period.startDate), "yyyy-MM-dd")
        : defaultRequest.checkIn,
      checkOut: filterValues?.period?.endDate
        ? asUTC(parseISO(filterValues.period.endDate), "yyyy-MM-dd")
        : defaultRequest.checkOut,
      concept: filterValues?.concept ?? defaultConcept,
      currency: currency === "original" ? "" : currency,
      dateSearchCriteria: filterValues?.dateSearchCriteria ?? defaultRequest.dateSearchCriteria,
      groupByEntity: filterValues?.groupByEntity ?? defaultRequest.groupByEntity,
      groupDateBy: filterValues?.groupDateBy ?? defaultRequest.groupDateBy,
      keywordFilter: filterValues?.keywordFilter
        ? {
            value: filterValues?.keywordFilter,
            columnsToFilter: ["hotelTicker", "groupName", "partner"],
          }
        : defaultRequest.keywordFilter,
      orderBy: orderBy,
      status: statusMap.status,
      statusConfig: statusMap.statusConfig,
      paymentTypes: filteredPaymentTypes,
      hotelTickers: propertyTickers,
      isBookingByStatus: type === ReportType.STATUS,
    },
    size: pageSize,
    page: currentPage,
  };
}

export function generateReportCsv(
  report: ReportCell[],
  locale: string,
  type: ReportType,
  isWitAdmin: boolean,
  groupByEntity?: string
): string {
  const headers = generateReportHeaders(type, isWitAdmin, groupByEntity);
  const cellDefinitions: ReportCellCSV[] = Object.keys(headers).map(it => ({
    message: reportMessages[it],
    value: entity => entity[it],
  }));
  const rowToCsv = (report: ReportCell) => cellDefinitions.map(it => it.value(report)).join(";");

  let rows = `${cellDefinitions.map(it => formatMessage(it.message, locale)).join(";")}\n`;
  rows += report.map(it => rowToCsv(it)).join("\n");

  return URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
}
